import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { PawPrint, EnvelopeSimple, Lock, User, ArrowRight, Translate } from "@phosphor-icons/react";

export default function Register() {
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/register", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] bg-white">
      <div className="flex flex-col p-8 lg:p-12 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-pf-primary text-white grid place-items-center shadow-card">
              <PawPrint size={22} weight="fill" />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight text-pf-text">
              Pawfile
            </span>
          </div>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-lg border border-pf-border px-2.5 py-1.5 text-xs font-semibold text-pf-text-secondary hover:bg-pf-bg transition-colors"
          >
            <Translate size={14} />
            <span className={lang === "en" ? "text-pf-primary" : ""}>EN</span>
            <span className="opacity-40">/</span>
            <span className={lang === "ro" ? "text-pf-primary" : ""}>RO</span>
          </button>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            {!submitted ? (
              <>
                <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-pf-text tracking-tight leading-tight">
                  {t("createAccount")}.
                </h1>
                <p className="mt-2 text-pf-text-secondary text-sm">
                  {t("signInHint")}
                </p>

                <form onSubmit={onSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="pf-label mb-1.5 block">{t("fullName")}</label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
                      <input
                        data-testid="register-name-input"
                        required
                        className="pf-input pl-9"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        autoComplete="name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="pf-label mb-1.5 block">{t("email")}</label>
                    <div className="relative">
                      <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
                      <input
                        data-testid="register-email-input"
                        type="email"
                        required
                        className="pf-input pl-9"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="pf-label mb-1.5 block">{t("password")}</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
                      <input
                        data-testid="register-password-input"
                        type="password"
                        required
                        minLength={6}
                        className="pf-input pl-9"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="mt-1 text-[11px] text-pf-text-secondary">{t("passwordMinChars")}</div>
                  </div>

                  {err && (
                    <div data-testid="register-error" className="text-sm text-pf-danger bg-pf-danger-bg border border-pf-danger/20 rounded-lg px-3 py-2">
                      {err}
                    </div>
                  )}

                  <button
                    data-testid="register-submit-btn"
                    type="submit"
                    disabled={busy}
                    className="pf-btn-primary w-full justify-center py-2.5"
                  >
                    {busy ? t("signingUp") : t("createAccount")} <ArrowRight size={16} />
                  </button>

                  <div className="text-center text-xs text-pf-text-secondary">
                    <a onClick={(e) => { e.preventDefault(); navigate("/login"); }} href="/login" className="hover:text-pf-primary hover:underline cursor-pointer">
                      {t("backToLogin")}
                    </a>
                  </div>
                </form>
              </>
            ) : (
              <div className="space-y-4" data-testid="register-success">
                <h1 className="text-4xl font-display font-extrabold text-pf-text tracking-tight leading-tight">
                  {t("registerSuccessTitle")}
                </h1>
                <p className="text-pf-text-secondary">{t("registerSuccessBody")}</p>
                <button onClick={() => navigate("/login")} className="pf-btn-primary">
                  {t("backToLogin")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="text-xs text-pf-text-secondary">
          © {new Date().getFullYear()} Pawfile • Veterinary practice management
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1682448169828-039868860001?auto=format&fit=crop&w=1400&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#17252A]/70 via-[#17252A]/30 to-transparent" />
      </div>
    </div>
  );
}
